import { useEffect, useState } from "react";
import { Button, Modal } from "rizzui";
import { IColorTag, IUserColorTag } from "@/utilities/types/ColorTag";
import ColorTagBadge from "./ColorTagBadge";
import { PiGear } from "react-icons/pi";
import UserRepository from "@/utilities/repositories/Users";

interface ColorTagSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userColorTags: IUserColorTag[];
  availableColorTags: IColorTag[];
  onOpenColorTagManager: () => void;
  refresh: () => Promise<void>;
}

export default function ColorTagSelector({
  isOpen,
  onClose,
  userId,
  userColorTags,
  availableColorTags,
  onOpenColorTagManager,
  refresh,
}: ColorTagSelectorProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [assignedTagIds, setAssignedTagIds] = useState<string[]>([]);

  const assignedTags = availableColorTags.filter((tag) =>
    assignedTagIds.includes(tag.id)
  );

  const unassignedTags = availableColorTags.filter(
    (tag) => !assignedTagIds.includes(tag.id)
  );

  const handleAssignTag = async (colorTagId: string) => {
    setIsLoading(true);
    try {
      const colorTagIds = new Set(assignedTagIds).add(colorTagId).values();
      await UserRepository.assignColorTag({
        userId,
        colorTagIds: [...colorTagIds],
      });
      setAssignedTagIds((pre) => [...new Set(pre).add(colorTagId).values()]);
      await refresh();
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveTag = async (colorTagId: string) => {
    setIsLoading(true);
    try {
      await UserRepository.assignColorTag({
        userId,
        colorTagIds: assignedTagIds.filter((id) => id !== colorTagId),
      });
      setAssignedTagIds((pre) => pre.filter((id) => id !== colorTagId));
      await refresh();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setAssignedTagIds(userColorTags.map((uct) => uct.colorTagId));
  }, [userColorTags]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Manage Color Tags
            </h3>
            <Button
              variant="outline"
              size="md"
              onClick={onOpenColorTagManager}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              Update colors tags
              <PiGear className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-sm text-gray-600">
            Assign color tags to categorize this user
          </p>
        </div>

        {/* Assigned Tags */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Assigned Tags ({assignedTags.length})
          </h4>
          {assignedTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {assignedTags.map((tag) => (
                <ColorTagBadge
                  key={tag.id}
                  colorTag={tag}
                  onRemove={() => handleRemoveTag(tag.id)}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No tags assigned</p>
          )}
        </div>

        {/* Available Tags */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Available Tags ({unassignedTags.length})
          </h4>
          {unassignedTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {unassignedTags.map((tag) => (
                <ColorTagBadge
                  key={tag.id}
                  colorTag={tag}
                  onClick={() => handleAssignTag(tag.id)}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              All available tags are already assigned
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
